/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { useState, useEffect, useRef } from 'react';
import type { FC } from 'react';

import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { icons } from '@/main/assets';
import { ERequestStatus } from '@/main/types/globalTypes';
import { Button, LinkBlock } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { FieldLabel } from '@components/common/FieldLabel/FieldLabel';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectEdmOperatorConnectionTranslation, makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import Modal from '@modules/modal/components/Modal';
import { EModalStatus } from '@modules/modal/types/modalTypes';
import { ClientId } from '@pages/PageAccountInfo/types';
import { makeSelectAuthoritiesIsServiceUser } from '@pages/PageAuth/selectors/authSelectors';
import { stringFormatParams } from '@utils/stringFormat';

import { ModalTextStyled, ModalTitleStyled, ModalTitleTextStyled } from '../../modal/components/styled/ModalStyled';
import { modalSetStatusAction } from '../../modal/modalActions';
import { EEdmOperatorConnectionMode } from '../constants';
import { useAddEdmConnectionMutation, useGetEdmOperatorsQuery } from '../store';
import type { IAddEdmConnectionForm } from '../types';
import { setConnectButtonDisable } from '../util/setEdmDisabled';
import { setEdmConnectionInitialValues } from '../util/setInitialFormValues';
import { setConnectButtonTooltip, setConnectButtonTooltipHoverListener, setConnectButtonTooltipMessage } from '../util/setTooltipTitle';
import { mapFormDataToAddEdmConnectionRequest } from '../util/transformEdmDelivery';
import { setAddConnectionValidationSchema } from '../util/validation';
import { EdmOperatorsSelector } from './EdmOperatorsSelector';
import { EdmRequestStatusModal } from './EdmRequestStatusModal';
import { EdmResponsibleContact } from './EdmResponsibleContact';

export interface IProps {
    clientId: string;
    initialValues?: IAddEdmConnectionForm;
    macroregionName?: string;
    mode?: EEdmOperatorConnectionMode;
    onCloseModal: () => void
};

export const EdmOperatorConnectionModal: FC<IProps> = (
    { clientId, initialValues, macroregionName, mode = EEdmOperatorConnectionMode.addOperator, onCloseModal },
) => {
    const dispatch = useDispatch();

    const [isShowContactFields, setIsShowContactFields] = useState(false);

    const {
        failure: tFailureConnection,
        start: tConnectionStart,
        success: tSuccessConnection,
    } = useSelector(makeSelectEdmOperatorConnectionTranslation());
    const tCommon = useSelector(makeSelectTranslationCommon());
    const isServiceUser = useSelector(makeSelectAuthoritiesIsServiceUser());

    const {
        data: operatorsOptions,
        isSuccess: operatorsOptionsLoaded,
        isLoading: operatorsOptionsLoading,
    } = useGetEdmOperatorsQuery();

    const [addEdmConnection,
        {
            isSuccess: successAddEdmConnection,
            isError: failureAddEdmConnection,
            isLoading: addEdmConnectionLoading,
            error: addEdmConnectionError,
        },
    ] = useAddEdmConnectionMutation();

    const initialFormValues = initialValues ?? setEdmConnectionInitialValues(operatorsOptions?.initialOption);
    const validationSchema = setAddConnectionValidationSchema(tCommon.form);
    const isChangeMode = mode === EEdmOperatorConnectionMode.changeOperator;

    const connectedOperator = useRef<string>('');
    const currentFormData = useRef<IAddEdmConnectionForm>(initialFormValues);

    useEffect(() => {
        if (isChangeMode && initialValues && operatorsOptions) {
            const currentOperator = operatorsOptions.options.find(
                operator => operator.value === initialValues.operator.value);
            if (currentOperator && currentOperator.isRoaming) {
                setIsShowContactFields(true);
            }
        }
    }, [initialValues, isChangeMode, operatorsOptions]);

    useEffect(() => {
        if (successAddEdmConnection) {
            dispatch(modalSetStatusAction({
                status: EModalStatus.success,
            }))
        }
    }, [dispatch, successAddEdmConnection]);

    useEffect(() => {
        if (failureAddEdmConnection) {
            dispatch(modalSetStatusAction({
                status: EModalStatus.failure,
            }))
        }
    }, [dispatch, failureAddEdmConnection]);

    const handleSubmit = (data: IAddEdmConnectionForm) => {
        const edmConnectionAddData = mapFormDataToAddEdmConnectionRequest(data);
        addEdmConnection({ bisAccountId: ClientId(clientId), edmConnectionAddData });
        connectedOperator.current = data.operator.label;
    };

    return (
        <Modal
            dataName='edm-operator-connection-modal'
            style={{ padding: '24px' }}
            width='480px'
        >
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.edmOperatorConnection]} />
            <Modal.Status status={EModalStatus.start}>
                <Modal.Title className='modal-popup-title__base'>
                    <ModalTitleStyled>
                        <ModalTitleTextStyled>
                            {tConnectionStart.selectOperatorTitle}
                        </ModalTitleTextStyled>
                        <ImgStyled height='16px' src={icons.cancelSmallIcon} width='16px' onClick={onCloseModal} />
                    </ModalTitleStyled>
                </Modal.Title>
                <ModalTextStyled>{macroregionName}</ModalTextStyled>
                <Loader showLoader={operatorsOptionsLoading || addEdmConnectionLoading}>
                    {operatorsOptionsLoaded && (
                        <Formik<IAddEdmConnectionForm>
                            enableReinitialize={isChangeMode}
                            initialValues={initialFormValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ isValid, submitForm, values }) => {
                                const isInitialOperator = initialValues?.operator.value === values.operator.value;

                                return (
                                    <>
                                        <Modal.Body style={{ maxHeight: 'initial', paddingTop: '16px' }}>
                                            <EdmOperatorsSelector
                                                currentFormData={currentFormData}
                                                initialOption={operatorsOptions.initialOption}
                                                isChangeMode={isChangeMode}
                                                options={operatorsOptions.options}
                                                setIsShowContactFields={setIsShowContactFields}
                                            />
                                            {isShowContactFields && (
                                                <>
                                                    <FieldLabel
                                                        className='close-icon-label'
                                                        name={tConnectionStart.responsibleContact}
                                                        prompt={tConnectionStart.responsibleContactTooltip}
                                                    />
                                                    <EdmResponsibleContact />
                                                </>
                                            )}
                                        </Modal.Body>
                                        <Modal.Control className='modal-popup-control__base'>
                                            <ArrowTooltip
                                                disableHoverListener={setConnectButtonTooltipHoverListener(
                                                    isServiceUser,
                                                    isChangeMode,
                                                    isInitialOperator,
                                                    isValid,
                                                )}
                                                message={setConnectButtonTooltipMessage(
                                                    isValid,
                                                    isChangeMode,
                                                    isInitialOperator,
                                                    isServiceUser,
                                                    tCommon.serviceUser.tooltip,
                                                )}
                                                title={setConnectButtonTooltip(
                                                    isValid,
                                                    isChangeMode,
                                                    isInitialOperator,
                                                    isServiceUser,
                                                    tConnectionStart,
                                                )}
                                            >
                                                <span>
                                                    <Button
                                                        disabled={
                                                            setConnectButtonDisable(
                                                                isChangeMode,
                                                                isInitialOperator,
                                                                isShowContactFields,
                                                                isValid,
                                                                isServiceUser,
                                                            )}
                                                        onClick={submitForm}
                                                    >
                                                        {tConnectionStart.connectButton}
                                                    </Button>
                                                </span>
                                            </ArrowTooltip>
                                            <LinkBlock onClick={onCloseModal}>
                                                {tCommon.modal.cancel}
                                            </LinkBlock>
                                        </Modal.Control>
                                    </>
                                )
                            }}
                        </Formik>
                    )}
                </Loader>
            </Modal.Status>
            <Modal.Status status={EModalStatus.success}>
                <EdmRequestStatusModal
                    mainText={tSuccessConnection.messageConnection}
                    mode={ERequestStatus.success}
                    title={
                        stringFormatParams(
                            tSuccessConnection.addConnectionTitle,
                            { operator: connectedOperator.current })
                    }
                    onCloseModal={onCloseModal}
                />
            </Modal.Status>
            <Modal.Status status={EModalStatus.failure}>
                <EdmRequestStatusModal
                    mode={ERequestStatus.fail}
                    requestError={addEdmConnectionError}
                    title={tFailureConnection.addConnectionTitle}
                />
            </Modal.Status>
        </Modal>
    );
};
